import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { trafficLight, fmtEUR, fmtDateShort } from "../lib/format";
import { Building2, User, Calendar, ChevronRight, Plus, Filter, X, Bookmark, BookmarkPlus, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import TimelineChart from "../components/TimelineChart";
import ImportExcelDialog from "../components/ImportExcelDialog";
import RevenuePie from "../components/RevenuePie";
import AnalyticsCharts from "../components/AnalyticsCharts";
import TrendChart from "../components/TrendChart";
import DashboardWidgets from "../components/DashboardWidgets";

const COLUMNS = [
  { key: "red", label: "Rosso — < 5 giorni", sub: "Urgente" },
  { key: "orange", label: "Arancione — 5-10 giorni", sub: "In arrivo" },
  { key: "yellow", label: "Giallo — > 10 giorni", sub: "Programmata" },
  { key: "green", label: "Verde — Consegnata", sub: "Chiusa" },
];

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterClient, setFilterClient] = useState("");
  const [filterType, setFilterType] = useState("");
  const [minAmt, setMinAmt] = useState("");
  const [maxAmt, setMaxAmt] = useState("");
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const nav = useNavigate();

  const onDragStart = (e, projectId) => {
    e.dataTransfer.setData("text/plain", projectId);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = async (e, targetCol) => {
    e.preventDefault();
    setDragOverCol(null);
    const pid = e.dataTransfer.getData("text/plain");
    if (!pid) return;
    const p = projects.find((x) => x.id === pid);
    if (!p) return;
    // Green column => mark delivered; other columns => not delivered
    const shouldDeliver = targetCol === "green";
    if (Boolean(p.delivered) === shouldDeliver) return; // no-op
    try {
      await api.put(`/projects/${pid}`, { delivered: shouldDeliver });
      toast.success(shouldDeliver ? "Marcata come consegnata" : "Riaperta");
      load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [pr, cl, st, vw] = await Promise.all([
        api.get("/projects"),
        api.get("/clients"),
        api.get("/dashboard/stats"),
        api.get("/saved-views"),
      ]);
      setProjects(pr.data);
      setClients(Object.fromEntries(cl.data.map((c) => [c.id, c])));
      setStats(st.data);
      setViews(vw.data);
    } catch (e) {
      toast.error("Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const applyView = (v) => {
    setActiveViewId(v.id);
    const f = v.filters || {};
    setFilterClient(f.filterClient || "");
    setFilterType(f.filterType || "");
    setMinAmt(f.minAmt || "");
    setMaxAmt(f.maxAmt || "");
  };
  const saveView = async () => {
    const name = window.prompt("Nome vista (es. 'PA sopra 500k'):");
    if (!name) return;
    try {
      const filters = { filterClient, filterType, minAmt, maxAmt };
      const r = await api.post("/saved-views", { name, filters });
      setViews([r.data, ...views]);
      setActiveViewId(r.data.id);
      toast.success("Vista salvata");
    } catch { toast.error("Errore"); }
  };
  const delView = async (v) => {
    if (!window.confirm(`Eliminare la vista "${v.name}"?`)) return;
    await api.delete(`/saved-views/${v.id}`);
    setViews(views.filter((x) => x.id !== v.id));
    if (activeViewId === v.id) setActiveViewId(null);
  };

  const bucketed = { red: [], orange: [], yellow: [], green: [], none: [] };
  const filtered = useMemo(() => {
    return projects.filter((p) => {
      if (filterClient && p.client_id !== filterClient) return false;
      if (filterType && p.type !== filterType) return false;
      const amt = Number(p.parcella_calculated || 0);
      if (minAmt && amt < Number(minAmt)) return false;
      if (maxAmt && amt > Number(maxAmt)) return false;
      return true;
    });
  }, [projects, filterClient, filterType, minAmt, maxAmt]);
  filtered.forEach((p) => {
    const tl = trafficLight(p.deadline, p.delivered);
    (bucketed[tl.level] || bucketed.none).push({ ...p, _tl: tl });
  });
  const activeFilters = filterClient || filterType || minAmt || maxAmt;
  const clearFilters = () => { setFilterClient(""); setFilterType(""); setMinAmt(""); setMaxAmt(""); setActiveViewId(null); };

  return (
    <div className="p-8 lg:p-10 space-y-8 fade-up">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="mono-label">Dashboard</div>
          <h1 className="font-display text-4xl font-medium text-neutral-900 mt-1">Cruscotto Gare</h1>
          <p className="text-sm text-neutral-500 mt-2">Semafori operativi su scadenze e consegne. Aggiornato in tempo reale.</p>
        </div>
        <div className="flex items-center gap-2">
          <ImportExcelDialog onDone={load} />
          <Button onClick={() => nav("/projects/new")} data-testid="new-project-btn" className="brand-bg hover:opacity-90">
            <Plus className="h-4 w-4 mr-1.5" /> Nuova gara
          </Button>
        </div>
      </header>

      {/* Overview: complessivo clienti e progetti */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border border-neutral-200 bg-white p-5" data-testid="overview-clients">
          <div className="mono-label">Clienti totali</div>
          <div className="font-display text-4xl font-medium mt-2 text-neutral-900">{stats?.clients_count ?? "—"}</div>
          <div className="mt-3 flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5"><User className="h-3 w-3 text-neutral-500" /><span className="font-mono font-semibold">{stats?.clients_private ?? 0}</span> privati</div>
            <div className="flex items-center gap-1.5"><Building2 className="h-3 w-3 text-neutral-500" /><span className="font-mono font-semibold">{stats?.clients_pa ?? 0}</span> PA</div>
          </div>
        </div>
        <div className="border border-neutral-200 bg-white p-5" data-testid="overview-projects">
          <div className="mono-label">Progetti totali</div>
          <div className="font-display text-4xl font-medium mt-2 text-neutral-900">{(stats?.projects_active ?? 0) + (stats?.projects_delivered ?? 0)}</div>
          <div className="mt-3 flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5"><Building2 className="h-3 w-3 text-neutral-500" /><span className="font-mono font-semibold">{stats?.projects_pa ?? 0}</span> gare PA</div>
            <div className="flex items-center gap-1.5"><User className="h-3 w-3 text-neutral-500" /><span className="font-mono font-semibold">{stats?.projects_private ?? 0}</span> privati</div>
          </div>
        </div>
        <div className="border border-neutral-200 bg-white p-5" data-testid="overview-status">
          <div className="mono-label">Stato gare</div>
          <div className="font-display text-4xl font-medium mt-2 text-neutral-900">{stats?.projects_active ?? "—"}</div>
          <div className="mt-3 flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5"><div className="h-2 w-2 tl-bar-red" /><span className="font-mono font-semibold text-red-700">{stats?.projects_red ?? 0}</span> urgenti</div>
            <div className="flex items-center gap-1.5"><div className="h-2 w-2 tl-bar-green" /><span className="font-mono font-semibold">{stats?.projects_delivered ?? 0}</span> consegnate</div>
          </div>
        </div>
        <div className="border border-neutral-200 bg-white p-5" data-testid="overview-cash">
          <div className="mono-label">Cash flow</div>
          <div className="font-mono text-2xl font-semibold mt-2 text-emerald-700">{fmtEUR(stats?.total_paid || 0)}</div>
          <div className="mt-3 flex items-center gap-4 text-xs">
            <div>Fatt: <span className="font-mono font-semibold text-blue-700">{fmtEUR(stats?.total_invoiced || 0)}</span></div>
            <div>Mat: <span className="font-mono font-semibold">{fmtEUR(stats?.total_accrued || 0)}</span></div>
          </div>
        </div>
      </div>

      {/* Stats detail row (compact) */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-0 border border-neutral-200 bg-white">
        {[
          { l: "Clienti", v: stats?.clients_count ?? "—" },
          { l: "Gare attive", v: stats?.projects_active ?? "—" },
          { l: "Consegnate", v: stats?.projects_delivered ?? "—" },
          { l: "Maturato", v: fmtEUR(stats?.total_accrued || 0) },
          { l: "Fatturato", v: fmtEUR(stats?.total_invoiced || 0) },
          { l: "Pagato", v: fmtEUR(stats?.total_paid || 0) },
        ].map((s, i) => (
          <div key={s.l} className={`p-5 ${i > 0 ? "border-l border-neutral-200" : ""}`} data-testid={`stat-${s.l.toLowerCase()}`}>
            <div className="mono-label">{s.l}</div>
            <div className="font-display text-2xl font-medium mt-2 text-neutral-900">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Alert Scadenza Link + Top Clienti */}
      <DashboardWidgets />

      {/* Filters */}
      <div className="border border-neutral-200 bg-white">
        {views.length > 0 && (
          <div className="px-4 py-2.5 border-b border-neutral-100 flex items-center gap-2 flex-wrap">
            <div className="mono-label flex items-center gap-1.5"><Bookmark className="h-3 w-3" /> Viste salvate</div>
            {views.map((v) => (
              <div key={v.id} className={`group flex items-center gap-1 border ${activeViewId === v.id ? "brand-border brand-text" : "border-neutral-300 text-neutral-700"}`}>
                <button onClick={() => applyView(v)} data-testid={`view-${v.id}`} className="px-2.5 py-1 text-xs">
                  {v.name}
                </button>
                <button onClick={() => delView(v)} className="px-1 text-neutral-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="p-4 flex items-center gap-3 flex-wrap">
          <div className="mono-label flex items-center gap-1.5"><Filter className="h-3 w-3" /> Filtri</div>
          <select
            value={filterClient}
            onChange={(e) => { setFilterClient(e.target.value); setActiveViewId(null); }}
            data-testid="filter-client"
            className="border border-neutral-300 px-2 py-1.5 text-sm bg-white"
          >
            <option value="">Tutti i clienti</option>
            {Object.values(clients).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value); setActiveViewId(null); }}
            data-testid="filter-type"
            className="border border-neutral-300 px-2 py-1.5 text-sm bg-white"
          >
            <option value="">Tutti i tipi</option>
            <option value="pa">Pubblica Amm.</option>
            <option value="private">Privato</option>
          </select>
          <div className="flex items-center gap-2">
            <span className="mono-label">Parcella €</span>
            <input type="number" placeholder="min" value={minAmt} onChange={(e) => { setMinAmt(e.target.value); setActiveViewId(null); }} data-testid="filter-min" className="w-24 border border-neutral-300 px-2 py-1.5 text-sm bg-white" />
            <span className="text-neutral-400">–</span>
            <input type="number" placeholder="max" value={maxAmt} onChange={(e) => { setMaxAmt(e.target.value); setActiveViewId(null); }} data-testid="filter-max" className="w-24 border border-neutral-300 px-2 py-1.5 text-sm bg-white" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            {activeFilters && !activeViewId && (
              <button onClick={saveView} data-testid="save-view-btn" className="text-xs brand-text hover:opacity-70 flex items-center gap-1">
                <BookmarkPlus className="h-3 w-3" /> Salva vista
              </button>
            )}
            {activeFilters && (
              <button onClick={clearFilters} data-testid="filter-clear" className="text-xs text-neutral-600 hover:text-neutral-900 flex items-center gap-1">
                <X className="h-3 w-3" /> Reset ({filtered.length} di {projects.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {COLUMNS.map((col) => (          <div
            key={col.key}
            onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.key); }}
            onDragLeave={() => setDragOverCol((c) => (c === col.key ? null : c))}
            onDrop={(e) => onDrop(e, col.key)}
            className={`border bg-white flex flex-col min-h-[400px] transition-colors ${dragOverCol === col.key ? "brand-border border-2" : "border-neutral-200"}`}
            data-testid={`kanban-column-${col.key}`}
          >
            <div className="p-4 border-b border-neutral-200 flex items-center gap-2 sticky top-0 bg-white z-10">
              <div className={`h-2.5 w-2.5 tl-bar-${col.key}`} />
              <div className="flex-1">
                <div className="text-sm font-semibold text-neutral-900">{col.label}</div>
                <div className="mono-label">{bucketed[col.key].length} gare · {col.sub}</div>
              </div>
            </div>
            <div className="p-3 space-y-2 flex-1">
              {bucketed[col.key].length === 0 && !loading && (
                <div className="text-xs text-neutral-400 py-8 text-center">
                  {dragOverCol === col.key ? "Rilascia qui" : "Nessuna gara"}
                </div>
              )}
              {bucketed[col.key].map((p) => {
                const cl = clients[p.client_id];
                return (
                  <Link
                    key={p.id}
                    to={`/projects/${p.id}`}
                    draggable
                    onDragStart={(e) => onDragStart(e, p.id)}
                    data-testid={`kanban-card-${p.id}`}
                    className={`relative block p-4 border border-neutral-200 hover:border-neutral-400 hover-lift bg-white cursor-grab active:cursor-grabbing`}
                  >
                    <div className={`absolute left-0 top-0 bottom-0 w-1 tl-bar-${col.key}`} />
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="mono-label flex items-center gap-1.5">
                        {p.type === "pa" ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
                        {p.type === "pa" ? "PA" : "Privato"}
                      </div>
                      <ChevronRight className="h-4 w-4 text-neutral-300" />
                    </div>
                    <div className="text-sm font-medium text-neutral-900 leading-tight line-clamp-2">{p.title}</div>
                    <div className="text-xs text-neutral-500 mt-1 truncate">{cl?.name || "—"}</div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-100">
                      <div className="flex items-center gap-1.5 text-xs text-neutral-600">
                        <Calendar className="h-3 w-3" /> {fmtDateShort(p.deadline)}
                      </div>
                      <div className={`text-xs px-2 py-0.5 border ${p._tl.className} font-mono font-semibold`}>
                        {p._tl.label}
                      </div>
                    </div>
                    <div className="mt-2 font-mono text-sm font-semibold text-neutral-900">
                      {fmtEUR(p.parcella_calculated)}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Timeline chart */}
      <TimelineChart />

      {/* Trend acquisito vs consegnato */}
      <TrendChart />

      {/* Analytics charts */}
      <AnalyticsCharts />

      {/* Revenue by client */}
      <RevenuePie />
    </div>
  );
}
